import type { SignOffField } from "../shared/api-types/training";

import request from "./request";
import { typedRequest } from "./typedRequest";
import { Person, PersonResponse, mapPerson, removeDuplicates } from "./base";
import { CrewShort, CrewShortResponse, mapCrewShort } from "./crew";

interface TrainingBase {
  displayname: string;
  courseId?: number;
  status: string;
  context: string;
  classroom: string;
  location?: string;
  trainingStartDate: string;
  trainingEndDate: string;
  trainingSlot: number;
  validityCalculation: string;

  // Attendance and completion flags
  classroomAttendance: number;
  classroomCheck: number;
  isClassroom: number;
  isELearning: number;
  isPractical: number;
  instructorComplete: number;
  assessorPassed: number;
  checkerPassed: number;
  eLearningCheck: number;
  writtenTest: number;

  // Links and resources
  classroomResourceLink?: string | null;
  virtualClassroomLink: string;

  // Training details
  classification: string;
  practicalTraining: string;
}

export interface TrainingResponse extends TrainingBase {
  id: number;
  positions?: string;

  classroomMoodle: number | null;
  eLearningMoodle: number | null;

  // Related entities
  crews: CrewShortResponse[];
  instructors: PersonResponse[];
  assessors: PersonResponse[];
  checkers: PersonResponse[];
  courses: any[];
  trainers: PersonResponse[];
}

interface TrainingRequest extends TrainingBase {
  classroomMoodle?: number;
  eLearningMoodle?: number;

  // Related entities
  courses: number[];
  crews: number[];
  trainers: number[];
  instructors: number[];
  assessors: number[];
  checkers: number[];
}

export class Training {
  public id!: number;
  public displayname!: string;
  public courseId!: number;
  public status!: string;
  public context!: string;
  public classroom!: string;
  public location!: string;
  public positions?: string;
  public trainingStartDate!: string;
  public trainingEndDate!: string;
  public trainingSlot!: number;
  public classroomAttendance!: number;
  public classroomCheck!: number;
  public isClassroom!: number;
  public isELearning!: number;
  public isPractical!: number;
  public instructorComplete!: number;
  public assessorPassed!: number;
  public checkerPassed!: number;
  public eLearningCheck!: number;
  public writtenTest!: number;
  public classroomMoodle!: number | null;
  public classroomResourceLink!: string | null;
  public eLearningMoodle!: number | null;
  public virtualClassroomLink!: string;
  public classification!: string;
  public practicalTraining!: string;
  public validityCalculation!: string;

  // Reference entity arrays - these need special handling
  public crews!: CrewShort[];
  public instructors!: Person[];
  public assessors!: Person[];
  public checkers!: Person[];
  public courses!: any[];
  public trainers!: Person[];

  constructor(data: TrainingResponse) {
    Object.assign(this, data);

    this.crews = data.crews?.map((crew) => mapCrewShort(crew)) || [];
    this.assessors = removeDuplicates(data.assessors?.map((assessor) => mapPerson(assessor))) || [];
    this.instructors = removeDuplicates(data.instructors?.map((instructor) => mapPerson(instructor))) || [];
    this.checkers = removeDuplicates(data.checkers?.map((checker) => mapPerson(checker))) || [];
    this.trainers = removeDuplicates(data.trainers?.map((trainer) => mapPerson(trainer))) || [];
  }
}

export const createTraining = async (training: TrainingRequest) => {
  const response = await request.post(`/trainings`, training);
  return response;
};

export const getTraining = async (trainingId: number): Promise<Training> => {
  const training: TrainingResponse = await request(`/trainings/${trainingId}`);
  return new Training(training);
};

export const updateTraining = async (trainingId: number, training: TrainingRequest) => {
  const response = await request.put(`/trainings/${trainingId}`, training);
  return response;
};

interface Observable {
  activities_training_id: number;
  activities_training_name: string;
  id: number;
  observable_id: number;
  observable_name: string;
  position_id: number;
  process_step_id: number;
  process_step_name: string;
  passed?: boolean;
}

export interface ProcessStep {
  ob: Observable[];
  process_step_id: number;
  process_step_name: string;
}

export interface PassedObservable {
  process_step_id: number;
  activities_training_id: number;
  observable_id: number;
}

export const getObservableByPositionId = async (positionId: number): Promise<ProcessStep[]> => {
  const json = await request(`/users-training/observable/${positionId}`);
  return json.map((processStep: any) => ({
    ...processStep,
    ob: processStep.ob.filter(
      (item: any) =>
        item.activities_training_name !== null && item.observable_id !== null && item.observable_name !== null,
    ),
  }));
};

export const getAssessmentPassed = async (trainingUserId: number): Promise<PassedObservable[]> => {
  const json = await request(`/users-training/assessment-passed/${trainingUserId}`);
  return json;
};

// TODO: replace any with a particular type once needed (it is too much effort to do it now with not enough gain)
export const getTrainingUser = async (trainingUserId: number): Promise<any> => {
  const usersTrainingProgress = await request(`/users-training/training-user-progress/${trainingUserId}`);
  return usersTrainingProgress;
};

export const isTrainingCompleted = (
  trainingDefinition: { is_e_learning: boolean; is_classroom: boolean; is_practical: boolean },
  userTraining: { elearning_status: boolean; classroom_status: boolean; practical_status: boolean },
): boolean => {
  const eLearningPassed = !trainingDefinition.is_e_learning || userTraining.elearning_status;
  const classroomPassed = !trainingDefinition.is_classroom || userTraining.classroom_status;
  const practicalPassed = !trainingDefinition.is_practical || userTraining.practical_status;
  return eLearningPassed && classroomPassed && practicalPassed;
};

export const isPracticalModuleCompleted = (
  trainingDefinition: { instructorComplete: number; assessorPassed: number; checkerPassed: number },
  userTraining: { instructor_complete: number; assessor_passed: number; checker_passed: number },
): boolean => {
  const instructorPassed = !trainingDefinition.instructorComplete || userTraining.instructor_complete === 1;
  const assessorPassed = !trainingDefinition.assessorPassed || userTraining.assessor_passed === 1;
  const checkerPassed = !trainingDefinition.checkerPassed || userTraining.checker_passed === 1;
  return instructorPassed && assessorPassed && checkerPassed;
};

/**
 * `classroomCheck` is the training definition's "a classroom test is required" flag
 * (`trainings.classroom_check`); `classroom_test` is the user's result
 * (`user_2_training.classroom_test`). There is no `classroom_check` column on the user row.
 */
export const isClassroomModuleCompleted = (
  trainingDefinition: {
    classroomAttendance: number;
    classroomCheck?: number;
  },
  userTraining: {
    classroom_attendance: number;
    classroom_test?: number;
  },
): boolean => {
  const attendancePassed = !trainingDefinition.classroomAttendance || userTraining.classroom_attendance === 1;
  const testPassed = !trainingDefinition.classroomCheck || userTraining.classroom_test === 1;
  return attendancePassed && testPassed;
};

/**
 * Corrects the recorded completion date of a sign-off that was already granted.
 * `date` is a calendar day (`YYYY-MM-DD`); the API rejects future dates.
 */
export const updateSignOffDate = async (trainingUserId: number, field: SignOffField, date: string) => {
  return typedRequest.put({
    url: "/users-training/checking-progress/:trainingUserId/date",
    params: { trainingUserId },
    body: { field, date },
  });
};
